import { useState, useEffect } from "react";
import { FaCheck, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExportOptions from "../../../ExportOptions";
import ConfirmActionModal from "../../../ConfirmDeleteModal";
import TableSkeleton from "../../../TableSkeleton";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../../services/pathologyCache";
import NicuBedModal from "./NicuBedModal";

const NicuBedList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bedModalOpen, setBedModalOpen] = useState(false);
  const [bedToEdit, setBedToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [beds, setBeds] = useState([]);
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const itemsPerPage = 10;

  const fetchBeds = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://api.muktihospital.com/api/nicu/bed", {
        headers: {
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
      });
      // console.log("Raw API response:", response.data); // Debug response
      const sanitizedBeds = response.data.beds.map((bed) => ({
        id: bed.id || Math.random().toString(36).substr(2, 9),
        bedNo: bed.bedNumber || "",
        wcCode: bed.wcCode || "",
        wcDetail: bed.wcDetail || "",
        rent: bed.rent || "",
      }));
      // console.log("Sanitized beds:", sanitizedBeds); // Debug sanitized data
      setBeds(sanitizedBeds);

      // Cache the bed list for offline viewing.
      setNicuOfflineActive(false);
      saveToCache("nicu_beds_list", sanitizedBeds);
    } catch (error) {
      console.error("Error fetching beds:", error);
      if (error.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache("nicu_beds_list");
        if (cached && Array.isArray(cached)) {
          setBeds(cached);
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved beds.", { autoClose: 2000 });
        } else {
          setNicuOfflineActive(false);
          toast.info("Offline: open this page online once to save it.", { autoClose: 3000 });
        }
      } else {
        toast.error("Failed to fetch beds.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBeds();
  }, [refreshTrigger]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredBeds = beds.filter(
    (bed) =>
      bed.bedNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bed.wcCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (bed.wcDetail || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredBeds.length / itemsPerPage);

  const currentData = filteredBeds
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((bed, index) => ({
      ...bed,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((bed) => bed.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (bedId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(bedId)
        ? prevSelectedRows.filter((id) => id !== bedId)
        : [...prevSelectedRows, bedId]
    );
  };

  const handleDelete = async (bedIds) => {
    setLoading(true);
    try {
      // Handle single or multiple deletions
      if (Array.isArray(bedIds)) {
        for (const id of bedIds) {
          await axios.delete(`https://api.muktihospital.com/api/nicu/bed/delete/${id}`, {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            },
          });
        }
      } else {
        await axios.delete(`https://api.muktihospital.com/api/nicu/bed/delete/${bedIds}`, {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        });
      }
      setRefreshTrigger((prev) => prev + 1);
      setSelectedRows([]);
      toast.success(
        `${Array.isArray(bedIds) ? bedIds.length : 1} bed(s) deleted successfully!`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } catch (error) {
      console.error("Error deleting bed:", error);
      if (error.response?.status === 404) {
        toast.error("Bed not found.", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("Failed to delete bed(s).", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

  const handleEdit = (bed) => {
    setBedToEdit(bed);
    setBedModalOpen(true);
  };

   const handleSaveBed = (savedBed, isEditMode) => {
    setRefreshTrigger((prev) => prev + 1);
    toast.success(`Bed ${isEditMode ? "updated" : "added"} successfully!`, {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["Serial No", "Bed No", "W/C Code", "W/C Detail", "Rent"];
  const dataMapper = (item) =>
    [item.displayId, item.bedNo, item.wcCode, item.wcDetail, item.rent].join("\t");

  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left print-hidden-in-print",
      skeletonClassName: "my-5 mx-3",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "150px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-right p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
  ];

  return (
    <div>
      <ToastContainer />
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Bed List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by bed number, W/C code, or W/C detail"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={() => {
                  setBedToEdit(null);
                  setBedModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Bed
              </button>
              <ExportOptions
                data={currentData}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Bed List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[800px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial No
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Bed No
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        W/C Code
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        W/C Detail
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Rent
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : filteredBeds.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">
                No beds found.
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[800px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              currentData.length > 0 &&
                              currentData.every((item) =>
                                selectedRows.includes(item.id)
                              )
                            }
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="agreement"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial No
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Bed No
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        W/C Code
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        W/C Detail
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Rent
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((bed, index) => (
                      <tr
                        key={bed.id}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                          <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={`bed-${index}`}
                              className="hidden peer print-hidden"
                              checked={selectedRows.includes(bed.id)}
                              onChange={() => handleRowSelect(bed.id)}
                              disabled={loading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={`bed-${index}`}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                            ></label>
                          </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {bed.displayId}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {bed.bedNo}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {bed.wcCode || "-"}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {bed.wcDetail || "-"}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {bed.rent}
                        </td>
                        <td className="p-3 text-right space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => handleEdit(bed)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([bed.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {currentPage * itemsPerPage - itemsPerPage + 1} to{" "}
                  {Math.min(
                    currentPage * itemsPerPage,
                    filteredBeds.length
                  )}{" "}
                  of {filteredBeds.length}
                </p>
                <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                  <li>
                    <button
                      className="table-pagination-item disabled:opacity-50"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <MdKeyboardArrowLeft />
                    </button>
                  </li>
                  {totalPages <= 10
                    ? [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i + 1
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    : (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;

                        for (
                          let i = 1;
                          i <= Math.min(firstPages, totalPages);
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        if (totalPages > firstPages + lastPages) {
                          pages.push(
                            <li
                              key="ellipsis"
                              className="table-pagination-item"
                            >
                              ...
                            </li>
                          );
                        }

                        for (
                          let i = Math.max(
                            firstPages + 1,
                            totalPages - lastPages + 1
                          );
                          i <= totalPages;
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        return pages;
                      })()}
                  <li>
                    <button
                      className="table-pagination-item"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <MdKeyboardArrowRight />
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="bed"
        title="Confirm Delete"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} bed(s)?`
            : `Do you really want to delete this bed?`
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <NicuBedModal
        isOpen={bedModalOpen}
        onClose={() => {
          setBedModalOpen(false);
          setBedToEdit(null);
        }}
        onSave={handleSaveBed}
        bedToEdit={bedToEdit}
      />
    </div>
  );
};

export default NicuBedList;